import { useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { RootState, useAppDispatch, useAppSelector } from '../store';
import { UserSettings } from '../Types';
import { setListReloadType } from '../Store/features/environment/environmentSlice';
import { promptService } from '../Services/promptService';
import { PaginationListType } from '../Types/PaginationParams';
import usePromptState from './state/usePromptState';
import useModal from '../components/Modal/useModal';
import { Prompt } from '../Types';
import { getBreakpoint } from '../utils/breakpoint';

const usePrompts = () => {
    const navigate = useNavigate();
    const userSettings = useAppSelector((state: RootState) => state.userSettings as UserSettings);
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);
    const demo = useAppSelector((state: RootState) => state.environment.demoInstance as boolean);
    const dispatch = useAppDispatch();
    const windowWidth = useAppSelector((state: RootState) => state.environment.windowWidth);
    const { prompts, setSelectedPrompt, setPrompts, updatePrompt, deletePrompt, getPromptValueById } = usePromptState();

    const modal = useModal();

    const notifySuccess = useCallback(
        (type: 'proposeGlobal' | 'makeGlobal' | 'unproposeGlobalSuccess') => {
            switch (type) {
                case 'proposeGlobal':
                    toast.success(i18n.translate({ id: 'toast.proposeGlobalSuccess', sourceName: 'Prompts' }));
                    break;
                case 'makeGlobal':
                    toast.success(i18n.translate({ id: 'toast.makeGlobalSuccess', sourceName: 'Prompts' }));
                    break;
                case 'unproposeGlobalSuccess':
                    toast.success(i18n.translate({ id: 'toast.unproposeGlobalSuccess', sourceName: 'Prompts' }));
                    break;
            }
        },
        [i18n],
    );

    const handleBasePromptChange = useCallback(
        (identifier: string) => {
            if (demo) return;

            promptService.setBasePrompt(identifier).then((response) => {
                setPrompts(response.data.data);
                dispatch(setListReloadType(PaginationListType.Assistants));
            });
        },
        [demo, dispatch, setPrompts],
    );

    const handleProposeGlobal = useCallback(
        (identifier: string) => {
            if (demo) {
                notifySuccess('proposeGlobal');
                return;
            }
            if (!userSettings.isLoggedIn) return;
            promptService.proposeGlobal(identifier).then(() => {
                updatePrompt(identifier, { proposedGlobal: true });
                notifySuccess('proposeGlobal');
            });
        },
        [demo, userSettings.isLoggedIn, notifySuccess, updatePrompt],
    );

    const handleRemoveProposedGlobal = useCallback(
        (identifier: string) => {
            if (demo) {
                notifySuccess('unproposeGlobalSuccess');
                return;
            }
            if (!userSettings.isLoggedIn) return;

            promptService.unproposeGlobal(identifier).then(() => {
                updatePrompt(identifier, { proposedGlobal: false });
                notifySuccess('unproposeGlobalSuccess');
            });
        },
        [demo, userSettings.isLoggedIn, notifySuccess, updatePrompt],
    );

    const handleMakeGlobal = useCallback(
        (identifier: string) => {
            if (demo) {
                notifySuccess('makeGlobal');
                return;
            }
            if (!userSettings.isLoggedIn) return;
            promptService.makeGlobal(identifier).then((response) => {
                if (response.data.data !== undefined) {
                    setPrompts(response.data.data);
                    dispatch(setListReloadType(PaginationListType.Assistants));
                    notifySuccess('makeGlobal');
                }
            });
        },
        [demo, userSettings.isLoggedIn, notifySuccess, dispatch, setPrompts],
    );

    const handleDeletePrompt = useCallback(
        (identifier: string) => {
            if (demo) {
                return;
            }
            if (!userSettings.isLoggedIn) return;
            promptService.deletePrompt(identifier).then(() => {
                deletePrompt(identifier);
                dispatch(setListReloadType(PaginationListType.Assistants));
            });
        },
        [demo, userSettings.isLoggedIn, deletePrompt, dispatch],
    );

    const handleOpenConfirmDialog = useCallback(
        (promptIdentifier: string) => {
            modal.deleteConfirm({
                title: i18n.translate({ id: 'promptDelete.title', sourceName: 'Prompts' }),
                content: i18n.translate({ id: 'promptDelete.text', sourceName: 'Prompts' }),
                onConfirm: () => handleDeletePrompt(promptIdentifier),
            });
        },
        [i18n, handleDeletePrompt, modal],
    );

    const handleOpenConfirmDialogBasePromptChange = useCallback(
        (promptIdentifier: string) => {
            modal.confirm({
                title: i18n.translate({ id: 'basePromptChange.title', sourceName: 'Prompts' }),
                content: i18n.translate({ id: 'basePromptChange.text', sourceName: 'Prompts' }),
                onConfirm: () => handleBasePromptChange(promptIdentifier),
            });
        },
        [i18n, handleBasePromptChange, modal],
    );

    const createNewChat = useCallback(
        async (promptIdentifier: string) => {
            setSelectedPrompt(getPromptValueById(promptIdentifier));
            navigate(`/start/${promptIdentifier}`);
        },
        [navigate, setSelectedPrompt, getPromptValueById],
    );

    const handleCopyPrompt = useCallback(
        async (prompt: Prompt) => {
            const response = await promptService.getPrompt(prompt.isUserPrompt, prompt.identifier);
            const fullPromptData = response.data;

            const promptCopy = {
                ...fullPromptData,
                name: `${fullPromptData.name} ${i18n.translate({
                    id: 'duplicate.copyTag',
                    sourceName: 'Main',
                })}`,
                identifier: undefined,
                files: [],
                isBasePrompt: false,
                proposedGlobal: false,
            };

            const path = prompt.isUserPrompt ? '/userassistants/new' : '/admin/assistants/new';
            navigate(path, { state: { duplicateData: promptCopy } });
        },
        [navigate, i18n],
    );

    const PromptsActions = useMemo(
        () => ({
            handleBasePromptChange,
            handleProposeGlobal,
            handleRemoveProposedGlobal,
            handleMakeGlobal,
            handleOpenConfirmDialog,
            handleOpenConfirmDialogBasePromptChange,
            createNewChat,
        }),
        [
            handleBasePromptChange,
            handleProposeGlobal,
            handleRemoveProposedGlobal,
            handleMakeGlobal,
            handleOpenConfirmDialog,
            handleOpenConfirmDialogBasePromptChange,
            createNewChat,
        ],
    );

    const handleNavigate = useCallback(
        (path: string) => {
            navigate(path);
        },
        [navigate],
    );

    return {
        PromptsActions,
        handleCopyPrompt,
        userSettings,
        demo,
        i18n,
        handleNavigate,
        prompts,
        windowWidth,
        getBreakpoint,
    };
};

export default usePrompts;
